import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMutation, gql } from "@apollo/client";
import { Tab } from "@headlessui/react";
import clsx from "clsx";
import { useToggle } from "usehooks-ts";

import Button from "components/Button";
import DataTable from "components/DataTable";
import Select from "components/Select";
import StatusModal from "components/StatusModal";
import StatusTabs from "components/StatusTabs";
import { BROKER_FR_STATUS_LABELS, CARRIER_FR_STATUS_LABELS } from "constants/base";
import { FR_CONTRACT_TYPE, MANAGE_PAY_TABLE_HEADER_LIST } from "constants/haulpay-broker";
import { HISTORY_TABLE_HEADER_LIST } from "constants/haulpay-carrier";
import { routes } from "constants/routes";
import {
  NEW_FUNDING_REQUEST_TYPE,
  NEW_STATUS_LIST
} from "features/main/HaulPay/modules/Broker/ManagePayList";
import {
  MUTATION_UPDATE_BROKER_FUNDING_REQUEST_STATUS,
  MUTATION_UPDATE_BROKER_FUNDING_REQUEST_TYPE
} from "features/main/Loads/Post/FundingRequest/query";
import { parseError } from "helpers/convertFormData";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import { useColorScheme } from "hooks/useColorScheme";
import usePaginatedQuery from "hooks/usePaginatedQuery";
import { useUserFeature } from "hooks/useUserFeature";
import { IOption } from "models/Form";
import { IFundingRequestItem, ISummaryData } from "models/HaulPay";
import { selectFactoringProfile } from "store/user/selector";
import { Filters } from "../../Broker/ManagePay";
import { QUERY_FUNDING_REQUEST_BY_ME } from "../../Broker/ManagePay/query";
import { QUERY_CARRIER_FUNDING_REQUEST_BY_ME } from "../../Carrier/ManagePay/query";
import SearchInput from "./components/SearchInput";
import styles from "./style.module.scss";
import { selectAccount } from "../../../../../store/auth/selector";
import FRTypeFilterDropdown from "../FRTypeFilterDropdown";
import { useAnalytics } from "hooks/useAnalytics";

type ListByStatusDesktopProps = {
  summaryData: ISummaryData;
  debtor?: string | null;
  refetchSummaryData: (data: any) => void;
  debtorName?: string | null;
};

const NEW_STATUS_LIST_WITH_HOLD = [
  ...NEW_STATUS_LIST,
  {
    id: NEW_STATUS_LIST.length + 1,
    label: "Hold",
    value: "hold"
  }
];

type FormValues = {
  state: IOption;
  type: IOption;
};

const VALIDATE_FUNDING_REQUESTS = gql`
  mutation ValidateFundingRequests(
    $requestIds: [String!]!
    $contractType: String
    $status: String
  ) {
    validateFundingRequests(requestIds: $requestIds, contractType: $contractType, status: $status) {
      results {
        id
        valid
        errors
      }
    }
  }
`;
type ValidateBrokerFundingRequestsVariables = {
  requestIds: string[];
  contractType?: string;
  status?: string;
};

export default function FRListByStatusDesktop({
  summaryData,
  debtor,
  refetchSummaryData,
  debtorName
}: Readonly<ListByStatusDesktopProps>) {
  const { currentIsDark } = useColorScheme();
  const { trackAllEvents } = useAnalytics();
  const { isBroker, isNonFactorRefactorEnabled } = useUserFeature();
  const FR_LABELS = isBroker ? BROKER_FR_STATUS_LABELS : CARRIER_FR_STATUS_LABELS;
  const navigate = useNavigate();
  const { userPermission } = useSelector(selectAccount);
  const factoringProfile = useSelector(selectFactoringProfile);
  const sortedSummary = useMemo(() => {
    return Object.keys(FR_LABELS).reduce<Record<string, any>>((acc, key) => {
      if (summaryData[key]) {
        acc[key] = summaryData[key];
      }
      return acc;
    }, {});
  }, [summaryData]);
  const defaultStatus =
    "incomplete" in sortedSummary ? "incomplete" : Object.keys(sortedSummary)[0] ?? "incomplete";
  const [filters, setFilters] = useState<Filters>({
    debtor: debtor ?? null,
    debtorName: debtorName ?? null,
    orderBy: "-created",
    status: defaultStatus,
    contractType: null
  });
  const [selectedFRs, setSelectedFRs] = useState<string[]>([]);
  const [errorList, setErrorList] = useState<string[]>([]);
  const [typeToUpdate, setTypeToUpdate] = useState<"status" | "type">("status");
  const [isSuccessModalOpen, toggleSuccessModal] = useToggle();
  const [clearFrTypeFilter, setClearFrTypeFilter] = useState(false);

  const {
    control,
    setValue,
    handleSubmit,
    getValues,
    formState: { isSubmitting }
  } = useForm<FormValues>({
    defaultValues: {
      state: NEW_STATUS_LIST[0],
      type: NEW_FUNDING_REQUEST_TYPE[0]
    }
  });

  const {
    data,
    loading,
    totalCount,
    error,
    loadNextPage,
    loadPreviousPage,
    setPageSize,
    goToPage,
    pageInfo,
    resetPagination
  } = usePaginatedQuery<Record<string, any>, IFundingRequestItem>({
    query: isBroker ? QUERY_FUNDING_REQUEST_BY_ME : QUERY_CARRIER_FUNDING_REQUEST_BY_ME,
    variables: {
      debtor: filters.debtor,
      ...(filters.status !== "all" && { status: filters.status }),
      orderBy: filters.orderBy,
      loadOrInvoiceOrDebtorOrStatus: filters.loadOrInvoiceOrDebtorOrStatus,
      contractType: filters.contractType
    },
    cursorField: "fundingRequestsByMe",
    pageSize: 20,
    options: {
      notifyOnNetworkStatusChange: true,
      skip: !filters.status
    },
    loadMore: true
  });

  useEffect(() => {
    if (error) showErrorMessage(parseError(error.message));
  }, [data, error, loading]);

  const [updateFRStatus] = useMutation(MUTATION_UPDATE_BROKER_FUNDING_REQUEST_STATUS, {
    onError(err) {
      err.graphQLErrors.forEach(({ message }) => showErrorMessage(parseError(message)));
    },
    onCompleted: () => {
      showSuccessMessage("Status updated successfully.");
    }
  });

  const [validateFRs] = useMutation(VALIDATE_FUNDING_REQUESTS, {
    onError: (err) => {
      if (err?.graphQLErrors)
        return err?.graphQLErrors.forEach(({ message }) => showErrorMessage(parseError(message)));
      if (Array.isArray(err)) return err.forEach((message) => showErrorMessage(message));
    },
    onCompleted: (data) => {
      const hasErrors = data.validateFundingRequests.results.some((r: any) => !r?.valid);
      if (hasErrors) {
        const results = data.validateFundingRequests.results || [];
        selectedFRs.map((fr, i) => {
          const frWithError = results.find((r: any) => r.id === fr && !r.valid);
          frWithError && setErrorList((prev) => [...prev, selectedFRs[i]]);
        });
        throw data.validateFundingRequests.results
          .filter((r: any) => !r?.valid)
          .flatMap((r: any) => r.errors.map((err: string) => err));
      }
    }
  });

  const [updateFRType] = useMutation(MUTATION_UPDATE_BROKER_FUNDING_REQUEST_TYPE, {
    onError(err) {
      err.graphQLErrors.forEach(({ message }) => showErrorMessage(parseError(message)));
    },
    onCompleted: () => {
      showSuccessMessage("Type updated successfully.");
    }
  });

  const goToCreateLoad = useCallback(() => {
    // "send_rate_confirmation_remote_approved" really means
    // "DO_NOT_send_rate_confirmation_remote_approved" 🤷🏻‍♂️
    if (!factoringProfile?.send_rate_confirmation_remote_approved) {
      navigate(routes.LOADS_POSTED_CREATE);
    } else {
      navigate(routes.HAULPAY_BROKER_FUNDING_REQUEST);
    }
  }, [navigate]);

  const handleSelectAll = useCallback(() => {
    if (selectedFRs?.length === data.length) setSelectedFRs([]);
    else {
      setSelectedFRs(data.map((fr: IFundingRequestItem) => fr.id));
    }
  }, [selectedFRs, data]);

  const toggleSelection = (id: string) => {
    if (selectedFRs.includes(id)) setSelectedFRs(selectedFRs.filter((fr) => fr !== id));
    else setSelectedFRs((prev) => [...prev, id]);
  };

  const onUpdateFR = useCallback(
    async ({ state, type }: FormValues) => {
      const variables: ValidateBrokerFundingRequestsVariables = {
        requestIds: selectedFRs
      };
      if (state.value === "none") {
        variables.contractType = type.value;
      } else if (type.value === "none") {
        variables.status = state.value.toUpperCase().replace(/\s+/g, "_");
      }
      let response: any[] = [];
      try {
        const validationResponse = await validateFRs({
          variables
        });
        if (validationResponse?.errors?.length) return;
        if (state.value === "none") {
          response = await Promise.all(
            selectedFRs.map((id) =>
              updateFRType({
                variables: {
                  id,
                  contractType: type.value,
                  selfFinanceRequested: type.label !== "Factored"
                }
              })
            )
          );
        } else if (type.value === "none") {
          response = await Promise.all(
            selectedFRs.map((id) =>
              updateFRStatus({
                variables: {
                  id,
                  status: state.value
                }
              })
            )
          );
        } else {
          showErrorMessage("Please select the correct item.");
        }
        if (response.length && response.find((item) => !item?.errors)) {
          setSelectedFRs([]);
          toggleSuccessModal();
        } else {
          for (let i = 0; i < selectedFRs.length; i++) {
            if (response[i]?.errors) {
              setErrorList((prev) => [...prev, selectedFRs[i]]);
            } else {
              setSelectedFRs((prev) => prev.filter((id) => id !== selectedFRs[i]));
            }
          }
        }
      } catch (error) {
        // Error handling is already managed in mutations
      }
    },
    [selectedFRs, updateFRStatus, updateFRType, toggleSuccessModal]
  );

  useEffect(() => {
    if (typeToUpdate === "status") {
      setValue("type", NEW_FUNDING_REQUEST_TYPE[0]);
    } else {
      setValue("state", NEW_STATUS_LIST[0]);
    }
  }, [typeToUpdate]);

  const updateFilter = useCallback(
    (key: keyof Filters, value: Filters[keyof Filters]) => {
      setFilters((prevFilters) => {
        if (prevFilters[key] === value) {
          return prevFilters;
        }
        return {
          ...prevFilters,
          [key]: value
        };
      });
    },
    [setFilters]
  );
  const handleSearch = useCallback(
    (value: string | null) => {
      resetFrTypeFilter();
      refetchSummaryData({
        debtor: filters.debtor,
        loadOrInvoiceOrDebtorOrStatus: value
      });
      setFilters((prevFilters) => ({
        ...prevFilters,
        loadOrInvoiceOrDebtorOrStatus: value,
        status: "all"
      }));
    },
    [setFilters, refetchSummaryData]
  );
  const resetSearch = useCallback(() => {
    resetFrTypeFilter();
    refetchSummaryData({
      debtor: filters.debtor,
      loadOrInvoiceOrDebtorOrStatus: ""
    });
    setFilters((prevFilters) => ({
      ...prevFilters,
      loadOrInvoiceOrDebtorOrStatus: "",
      status: "all"
    }));
  }, [setFilters, refetchSummaryData]);

  const handleFrTypeFilterChange = (selectedFrType: IOption) => {
    if (selectedFrType.value !== filters.contractType) {
      resetPagination();
      refetchSummaryData({
        status: filters.status,
        contractType: selectedFrType.value
      });
      setFilters((prevFilters) => ({
        ...prevFilters,
        status: filters.status,
        contractType: selectedFrType.value
      }));
      setClearFrTypeFilter(true);
    }
  };

  const resetFrTypeFilter = () => {
    resetPagination();
    refetchSummaryData({
      status: filters.status,
      contractType: null
    });
    setFilters((prevFilters) => ({
      ...prevFilters,
      status: filters.status,
      contractType: null
    }));
    setClearFrTypeFilter(false);
  };

  const handleLinkClick = useCallback(() => {
    navigate(routes.HAULPAY_BROKER_MANAGE_PAY, {
      replace: true,
      state: {
        forceUpdate: true
      }
    });
  }, [navigate]);

  const frTypeOptions = FR_CONTRACT_TYPE.filter(
    (request) => userPermission.available_contract_types[request.value]
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        {isBroker && <Button onClick={goToCreateLoad}>Create Funding Request</Button>}
        <SearchInput
          initialValue={filters.loadOrInvoiceOrDebtorOrStatus || ""}
          onSearch={handleSearch}
          resetSearch={resetSearch}
        />
        {isBroker && isNonFactorRefactorEnabled && frTypeOptions.length > 0 && (
          <FRTypeFilterDropdown
            frTypeOptions={frTypeOptions}
            handleOnChange={handleFrTypeFilterChange}
            handleResetClick={resetFrTypeFilter}
            clearFilter={clearFrTypeFilter}
          />
        )}
      </div>
      {!!filters.debtorName && (
        <div className={styles.content}>
          <p>Displaying funding requests for {debtorName}</p>
          <button onClick={handleLinkClick} className={styles.clearFilter}>
            Clear Filter
          </button>
        </div>
      )}

      <Tab.Group
        selectedIndex={Object.keys(sortedSummary).indexOf(filters?.status ?? "")}
        onChange={(index) => {
          resetPagination();
          updateFilter("status", Object.keys(sortedSummary)[index]);
          trackAllEvents(`broker_managepay_${Object.keys(sortedSummary)[index] ?? "all"}`);
        }}>
        <StatusTabs tabsData={sortedSummary} summaryLabels={FR_LABELS} />

        <Tab.Panels>
          {Object.keys(sortedSummary).map((key) => (
            <Tab.Panel key={key} className="outline-none">
              {isBroker ? (
                <DataTable
                  headers={MANAGE_PAY_TABLE_HEADER_LIST}
                  data={data ?? []}
                  isShowChecked
                  handleSelectAll={handleSelectAll}
                  selectedList={selectedFRs}
                  handleClick={toggleSelection}
                  sortDisableList={["id"]}
                  totalCount={totalCount}
                  nextLink={pageInfo.hasNextPage}
                  prevLink={pageInfo.hasPreviousPage}
                  setPageSize={setPageSize}
                  goToPage={goToPage}
                  fetchPrevPage={loadPreviousPage}
                  fetchNextPage={loadNextPage}
                  loading={loading}
                  errorList={errorList}
                  infiniteScroll
                />
              ) : (
                <DataTable
                  headers={HISTORY_TABLE_HEADER_LIST}
                  data={data}
                  sortDisableList={["id"]}
                  totalCount={totalCount}
                  nextLink={pageInfo.hasNextPage}
                  prevLink={pageInfo.hasPreviousPage}
                  setPageSize={setPageSize}
                  goToPage={goToPage}
                  fetchPrevPage={loadPreviousPage}
                  fetchNextPage={loadNextPage}
                  loading={loading}
                  infiniteScroll
                />
              )}
            </Tab.Panel>
          ))}
        </Tab.Panels>
      </Tab.Group>
      {isBroker && selectedFRs.length > 0 ? (
        <form
          className={clsx(
            styles.footerContainer,
            !currentIsDark ? "drop-shadow-[0_-4px_4px_#B5B5B533]" : ""
          )}
          onSubmit={handleSubmit(onUpdateFR)}>
          <div className={styles.content}>
            <div className={styles.selectAll}>
              <span className="text-sm">Set Status</span>
              <Controller
                name="state"
                control={control}
                render={({ field: { value = NEW_STATUS_LIST[1], onChange } }) => (
                  <Select
                    value={value}
                    onChange={(value: IOption) => {
                      setTypeToUpdate("status");
                      onChange(value);
                    }}
                    options={
                      factoringProfile.enable_hold_review_funding_request_status
                        ? NEW_STATUS_LIST_WITH_HOLD
                        : NEW_STATUS_LIST
                    }
                    direction="up"
                    className="!w-60"
                  />
                )}
              />
            </div>
            <Button
              type="submit"
              rounded
              className={clsx(
                styles.submitButton,
                typeToUpdate === "type"
                  ? "!bg-subtitle !ring-subtitle cursor-not-allowed"
                  : "bg-gradient-to-r from-primary to-secondary"
              )}
              disabled={typeToUpdate === "type"}
              loading={isSubmitting}>
              SUBMIT
            </Button>
          </div>
          <div className={styles.content}>
            <div className={styles.selectAll}>
              <span className="text-sm">Set Type</span>
              <Controller
                name="type"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    value={value}
                    onChange={(value: IOption) => {
                      setTypeToUpdate("type");
                      onChange(value);
                    }}
                    options={NEW_FUNDING_REQUEST_TYPE}
                    direction="up"
                    className="!w-60"
                  />
                )}
              />
            </div>

            <Button
              type="submit"
              rounded
              className={clsx(
                styles.submitButton,
                typeToUpdate === "status"
                  ? "!bg-subtitle !ring-subtitle cursor-not-allowed"
                  : "bg-gradient-to-l from-primary to-secondary"
              )}
              disabled={typeToUpdate === "status"}
              loading={isSubmitting}>
              SUBMIT
            </Button>
          </div>
        </form>
      ) : null}
      {isBroker && (
        <StatusModal
          title="Status Updated"
          content={`Successfully set ${typeToUpdate} to ${typeToUpdate === "status" ? getValues("state.label") : getValues("type.label")}.`}
          isModalOpen={isSuccessModalOpen}
          toggleModal={toggleSuccessModal}
          backdropClick={toggleSuccessModal}
        />
      )}
    </div>
  );
}
